import { MessageSquare } from 'lucide-react';
import { Badge, Button } from '@roger/ui-components';
import { useCareTeamChannel, useInbox } from '../../hooks/chat/useChatQueries';
import { useStore } from '../../store';
import { FeatureFlag } from '../../constants/featureFlags';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

interface CareTeamChatButtonProps {
  episodeId: string;
}

/**
 * The chart's entry point into the episode's care-team conversation. It renders
 * only while the by-episode query holds a 200, so a non-member sees no button
 * and learns nothing.
 *
 * Gated by the WorkOS `chat` flag before any Query hook runs, the same way
 * ChatLauncher.tsx splits its gate from its hooks: `useCareTeamChannel` is
 * itself `enabled`-gated on the flag, but `useInbox` is not, so this
 * component must not call it for a flag-off user — that would fire a 403
 * GET /chat/inbox on every chart load.
 */
export default function CareTeamChatButton({
  episodeId,
}: CareTeamChatButtonProps) {
  const hasChat = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.CHAT, 'workos')
  );
  if (!hasChat) return null;
  return <CareTeamChatButtonInner episodeId={episodeId} />;
}

function CareTeamChatButtonInner({ episodeId }: CareTeamChatButtonProps) {
  const { data: channel, refetch } = useCareTeamChannel(episodeId);
  const { data: inbox } = useInbox();
  const isChatOpen = useStore(state => state.isChatOpen);
  const openChatChannel = useStore(state => state.openChatChannel);
  const showError = useStore(state => state.showError);

  if (!channel) return null;

  const unread =
    inbox?.find(row => row.channel_id === channel.id)?.unread_count ?? 0;
  // An archived conversation has no unread count of its own (the spec: no
  // pill when archived), even if a stale inbox row still carries one.
  const showPill = !channel.archived && !isChatOpen && unread > 0;

  return (
    <Button
      type='button'
      variant='outline'
      size='sm'
      onClick={async () => {
        // Membership can end while the chart is open (EMR-2007). Ask again
        // before opening: a 404 now reads as no channel, so the button goes
        // away and nothing opens or toasts. Any other failure keeps the old
        // cached channel in `data`, so open only on a successful re-check.
        const result = await refetch();
        if (result.isSuccess) {
          if (result.data) openChatChannel(result.data.id);
          return;
        }
        // The re-check failed, so nothing here knows whether the conversation
        // is still there. Say so rather than leave the press silent.
        showError(
          'Failed to open the conversation',
          getBusinessErrorMessage(result.error) ?? undefined
        );
      }}
    >
      <MessageSquare className='h-4 w-4' />
      Care team chat
      {showPill && <Badge variant='default'>{unread}</Badge>}
    </Button>
  );
}
